import React, { useState } from "react";
import { Star } from "lucide-react";
import CheckList from "components/checkList/CheckList";
import FeatureBadge from "components/featureBadge/FeatureBadge";
import ModalCloseButton from "components/modal/components/closeButton";

// Per browser: a manager who closed the introduction does not see it again. The suffix changes
// with each new version of the card, so closing an older one does not hide this one.
const DISMISSED_KEY = "missionTemplates.introDismissed.v2";

const readDismissed = () => {
    try {
        return localStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
        return false;
    }
};

/**
 * Introduction of the job templates, on the right of the list: a white card with a star tile,
 * a catchy title, what a template is for in one line, then its three benefits under the badges of
 * the temp worker app (CheckList), and the modals' close button. Closing hides it for good.
 */
const TemplatesIntro = ({ eyebrow, title, description, benefits = [], closeLabel, className = "" }) => {
    const [isDismissed, setIsDismissed] = useState(readDismissed);

    if (isDismissed) return null;

    const dismiss = () => {
        setIsDismissed(true);
        try {
            localStorage.setItem(DISMISSED_KEY, "1");
        } catch {
            // Storage unavailable: hidden until the next visit only.
        }
    };

    return (
        <aside className={`flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5 ${className}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="corner-smooth-12 flex h-11 w-11 shrink-0 items-center justify-center bg-yellow-50">
                    <Star size={22} color="#FF9600" fill="#FF9600" aria-hidden="true" />
                </div>
                <ModalCloseButton onClick={dismiss} label={closeLabel} size="sm" />
            </div>
            <div className="flex flex-col gap-1">
                {eyebrow ? (
                    <div className="mb-1">
                        <FeatureBadge>{eyebrow}</FeatureBadge>
                    </div>
                ) : null}
                <h2 className="text-lg font-semibold leading-6 tracking-[-0.3px] text-gray-900">{title}</h2>
                <p className="text-sm leading-5 text-gray-600">{description}</p>
            </div>
            {/* The temp worker app's CheckList: badges with their glint, one after the other. */}
            {benefits.length ? <CheckList items={benefits} /> : null}
        </aside>
    );
};

export default TemplatesIntro;
